import { useCallback, useEffect, useRef, useState } from "react";
import { getPipelineRunStatus } from "../../../services/classificationPipeline";
import { isReadyForReport } from "../constants/pipelineConstants";

const POLL_INTERVAL_MS = 30000;

/** Shape API pipeline_runs for DataGrid — one row per company in the batch (running or finished). */
function mapPipelineRuns(rawRuns) {
  return rawRuns.map((row) => ({
    id: row.company_name,
    company_name: row.company_name,
    status: row.status ?? "",
    task_state: row.task_state ?? "",
    error_count: row.error_count ?? "",
    retry_count: row.retry_count ?? "",
    last_updated: row.last_updated ?? "",
    log_stream_name: row.log_stream_name ?? "",
  }));
}

/**
 * Fetches GET /get-pipeline-run-status for the selected batch.
 * Polls every 30s while `enabled` until every company row is terminal.
 *
 * @param {string|null} batchRunId - Celery task_id for this batch run
 * @param {number} expectedCount - Number of companies submitted (guards partial Mongo state)
 * @param {{ enabled?: boolean, onReadyForReport?: (rows: object[]) => void }} [options]
 * @returns {{ pipelineRuns: object[], geocoderLogs: object[] }}
 */
export function usePipelineRunPolling(batchRunId, expectedCount, { enabled, onReadyForReport } = {}) {
  // All companies in this batch — not just active ones; grows as Mongo docs appear
  const [pipelineRuns, setPipelineRuns] = useState([]);
  const [geocoderLogs, setGeocoderLogs] = useState([]);
  const [pollingComplete, setPollingComplete] = useState(false);
  // Ref (not state) — fetch is async; skip setState if user navigated away before it returned
  const isMountedRef = useRef(true);
  // Parent passes a new onReadyForReport each render; ref keeps latest fn without restarting the interval
  const reportPhaseCallbackRef = useRef(onReadyForReport);
  // All companies finished — call report callback once, not on every poll that still sees terminal rows
  const hasTriggeredReportRef = useRef(false);

  reportPhaseCallbackRef.current = onReadyForReport;
  // Mirror `enabled` in a ref so fetchStatus can skip the report callback
  // for historical (completed) views without restarting the interval.
  const pollingEnabledRef = useRef(enabled);
  pollingEnabledRef.current = enabled;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // New batch_run_id — clear stale rows and allow report callback again
  useEffect(() => {
    setPipelineRuns([]);
    setGeocoderLogs([]);
    setPollingComplete(false);
    hasTriggeredReportRef.current = false;
  }, [batchRunId]);

  /** Fetch per-company rows from Mongo; stop polling and hand off to report phase when all are terminal. */
  const fetchStatus = useCallback(async () => {
    if (!batchRunId) {
      return;
    }

    const response = await getPipelineRunStatus(batchRunId);
    if (!isMountedRef.current) {
      return;
    }
    if (!(response instanceof Response) || !response.ok) {
      return; // 403 etc. from handleHttpResponse — skip update
    }

    const body = await response.json();
    const rows = mapPipelineRuns(body.pipeline_runs ?? []);
    setPipelineRuns(rows);
    setGeocoderLogs(body.geocoder_logs ?? []);

    if (isReadyForReport(rows, expectedCount) && !hasTriggeredReportRef.current) {
      hasTriggeredReportRef.current = true;
      setPollingComplete(true);
      // Historical views fetch once; only a live poll should request the report.
      if (pollingEnabledRef.current) {
        reportPhaseCallbackRef.current?.(rows);
      }
    }
  }, [batchRunId, expectedCount]);

  // Snapshot whenever a run is selected (live or historical)
  useEffect(() => {
    if (!batchRunId) {
      return;
    }
    fetchStatus();
  }, [batchRunId, fetchStatus]);

  // Interval only while the selected batch is still running
  useEffect(() => {
    let intervalId = null;

    if (enabled && batchRunId && !pollingComplete) {
      intervalId = setInterval(fetchStatus, POLL_INTERVAL_MS);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [enabled, batchRunId, pollingComplete, fetchStatus]);

  return { pipelineRuns, geocoderLogs };
}
